/**
 * LegacyMidiBridge.ts
 * 
 * This module provides backward compatibility with older native iOS apps that use
 * the legacy IosWebMidi.m bridge. Those apps inject JavaScript to call global
 * callback functions like `_callback_receiveMIDIMessage()`.
 * 
 * The new MIDIKit-based native app uses a different communication channel
 * (via nativeBridge.handleMessageFromNative), but both paths ultimately
 * emit events to the same midiEventBus.
 * 
 * @see documentation/midi/midi-backward-compatibility-plan.md
 */

import {
  midiNotePlayed,
  midiNoteStopped,
  onMidiFootPedal_pressed,
  onMidiFootPedal_released,
  onMidiDeviceStateChange,
} from "../eventBus/midi/midiEventBus";
import { logger } from "../log/logger";
import { settingsStore } from "../../redux/settings/settingsStore";
import { NativeMidiInput } from "../../models/NativeMidiInput";
import { NativeMidiInputArray } from "../../models/NativeMidiInputArray";
import { isIgnoredMidiDevice } from "./midiDeviceFilters";

// ============================================================
// Types
// ============================================================

/**
 * Legacy port format from old native IosWebMidi.m
 * This matches the JSON structure sent by _callback_onReady
 */
interface LegacyMidiPort {
  id: string;
  name: string;
  manufacturer: string;
}

// ============================================================
// Module State
// ============================================================

/** Map of port index to NativeMidiInput for tracking devices from old native */
const legacyDevices: Map<number, NativeMidiInput> = new Map();

/** Flag to prevent duplicate initialization */
let isInitialized = false;

// ============================================================
// Global Type Declarations
// ============================================================

declare global {
  interface Window {
    _callback_onReady: (sources: LegacyMidiPort[], destinations: LegacyMidiPort[]) => void;
    _callback_receiveMIDIMessage: (index: number, time: number, data: number[]) => void;
    _callback_midiStateChanged: () => void;
    _callback_onNotReady: () => void;
  }
}

// ============================================================
// Callback Handlers
// ============================================================

/**
 * Called by old native when MIDI is ready with a list of sources and destinations.
 * This is triggered in response to the "webmidipolyfill.onready" message.
 */
function handleOnReady(sources: LegacyMidiPort[], destinations: LegacyMidiPort[]): void {
  logger.midiAccess.info({
    message: `Legacy MIDI bridge: _callback_onReady received ${sources.length} source(s), ${destinations.length} destination(s)`,
  });

  // Clear existing devices and rebuild from sources
  legacyDevices.clear();
  const nativeInputArray = new NativeMidiInputArray();

  for (let i = 0; i < sources.length; i++) {
    const source = sources[i];
    
    // Filter out network sessions - these are iOS artifacts that we don't want to expose to users
    if (isIgnoredMidiDevice(source.name)) {
      logger.midiAccess.info({
        message: `Legacy MIDI bridge: Ignoring network session device "${source.name}"`,
      });
      continue;
    }
    
    // Convert string ID to number for consistency with NativeMidiInput interface
    const numericId = parseInt(source.id, 10) || i;
    
    const nativeInput = new NativeMidiInput({
      id: numericId,
      name: source.name,
      manufacturer: source.manufacturer,
    });
    
    legacyDevices.set(i, nativeInput);
    nativeInputArray.push(nativeInput);
  }

  // Update settings store with connection status and device options
  settingsStore.setIsMidiDeviceConnected(nativeInputArray.length > 0);
  settingsStore.setMidiDeviceOptions(nativeInputArray);

  // Notify listeners of the change
  const changedDevice = nativeInputArray.length > 0 ? nativeInputArray[0] : undefined;
  onMidiDeviceStateChange(changedDevice);
}

/**
 * Called by old native when a MIDI message is received.
 * Parses raw MIDI bytes and emits appropriate events to midiEventBus.
 * 
 * @param index - Port index (which MIDI source sent the message)
 * @param time - Timestamp in milliseconds
 * @param data - Raw MIDI bytes, e.g. [144, 60, 100] for Note On C4 velocity 100
 */
function handleReceiveMIDIMessage(index: number, time: number, data: number[]): void {
  const [command, noteNumber, velocity = 0] = data;
  
  // Parse MIDI command byte
  // Upper 4 bits = command type, lower 4 bits = channel (0-15)
  const commandShifted = command >> 4;
  const channel = (command & 0x0f) + 1; // Convert 0-15 to 1-16
  
  // MIDI command types
  const NOTE_OFF = 8;
  const NOTE_ON = 9;
  const CONTROL_CHANGE = 11;
  
  const isNoteOn = commandShifted === NOTE_ON && velocity !== 0;
  const isNoteOff = commandShifted === NOTE_OFF || (commandShifted === NOTE_ON && velocity === 0);
  const isControlChange = commandShifted === CONTROL_CHANGE;
  const isSustainPedal = isControlChange && noteNumber === 64;
  
  // Get or create a device reference for this port index
  const input = getOrCreateLegacyDevice(index);
  
  if (isNoteOn) {
    logger.midiNoteAction.info({
      message: `Legacy MIDI note played: { pitch: ${noteNumber}, velocity: ${velocity}, index: ${index} }`,
    });
    
    midiNotePlayed(
      noteNumber,
      velocity,
      input.getId(),
      channel,
      time,
      input.getId(),
      input,
      input.getName()
    );
  } else if (isNoteOff) {
    midiNoteStopped(
      noteNumber,
      input.getId(),
      channel,
      time,
      input.getId(),
      input,
      input.getName()
    );
  } else if (isSustainPedal) {
    // CC 64 = sustain pedal
    if (velocity > 0) {
      onMidiFootPedal_pressed(noteNumber, input.getId(), channel, time, input.getId(), input.getName());
    } else {
      onMidiFootPedal_released(noteNumber, input.getId(), channel, time, input.getId(), input.getName());
    }
  }
}

/**
 * Called by old native when MIDI device state changes (connect/disconnect).
 * The old native will follow this with _callback_onReady to provide the new device list.
 */
function handleMidiStateChanged(): void {
  logger.midiConnectionEvent.info({
    message: "Legacy MIDI bridge: _callback_midiStateChanged received",
  });
  
  // The old native typically calls _callback_onReady after this to provide the new device list.
  // We emit a state change event here to notify listeners that something changed.
  onMidiDeviceStateChange(undefined);
}

/**
 * Called by old native when MIDI is not available.
 */
function handleOnNotReady(): void {
  logger.midiAccess.info({
    message: "Legacy MIDI bridge: _callback_onNotReady received - MIDI not available",
  });
  
  // Clear devices and update state
  legacyDevices.clear();
  settingsStore.setIsMidiDeviceConnected(false);
  settingsStore.setMidiDeviceOptions(new NativeMidiInputArray());
}

// ============================================================
// Helper Functions
// ============================================================

/**
 * Get or create a NativeMidiInput for the given port index.
 * Creates a placeholder if the device isn't in our map yet.
 */
function getOrCreateLegacyDevice(index: number): NativeMidiInput {
  const existing = legacyDevices.get(index);
  if (existing) {
    return existing;
  }
  
  // Create a placeholder - this can happen if we receive a note event
  // before _callback_onReady provides the device list
  logger.midiAccess.info({
    message: `Legacy MIDI bridge: Device index ${index} not in map, creating placeholder`,
  });
  
  const placeholder = new NativeMidiInput({
    id: index,
    name: "MIDI Keyboard",
    manufacturer: "Unknown",
  });
  
  legacyDevices.set(index, placeholder);
  return placeholder;
}

// ============================================================
// Public API
// ============================================================

/**
 * Initialize the legacy MIDI bridge by registering global callback functions.
 * This should be called during app initialization on iOS to support older native apps.
 * 
 * Safe to call multiple times - subsequent calls are no-ops.
 */
export function initializeLegacyMidiBridge(): void {
  if (isInitialized) {
    logger.midiAccess.info({
      message: "Legacy MIDI bridge: Already initialized, skipping",
    });
    return;
  }
  
  // Register global callbacks that old native app calls via JavaScript injection
  window._callback_onReady = handleOnReady;
  window._callback_receiveMIDIMessage = handleReceiveMIDIMessage;
  window._callback_midiStateChanged = handleMidiStateChanged;
  window._callback_onNotReady = handleOnNotReady;
  
  isInitialized = true;
  
  logger.midiAccess.info({
    message: "Legacy MIDI bridge: Global callbacks registered (_callback_onReady, _callback_receiveMIDIMessage, _callback_midiStateChanged, _callback_onNotReady)",
  });
}

export const legacyMidiBridge = {
  initializeLegacyMidiBridge,
};

